import { BoxV4, PlanType } from '@labrav/react-components'

import { Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMemo } from 'react'

import clsx from 'clsx'
import {
  CombinedFormDimension,
  CombinedFormState,
} from '../../../../modules/combinedForm/reducer'
import { Product } from '../../../../modules/productsListing/reducer'
import { ShortenedNameV2 } from '@labrav/common'
import { faRuler } from '@fortawesome/pro-regular-svg-icons'

import { getCurrencySymbol } from '../../../../common/utils/getCurrencySymbol'
import { useStyles } from './UsagePlansView.styles'
import { useSelector } from '../../../../store'

interface UsagePlansViewProps {
  productType?: PlanType | undefined
  combinedFormData: CombinedFormState | undefined
  viewingPersona?: string
}

export const UsagePlansView = ({
  productType,
  combinedFormData,
  viewingPersona,
}: UsagePlansViewProps) => {
  const isCp = viewingPersona === 'CP'
  const { classes } = useStyles({ productType, isCp })
  const dimensions = (combinedFormData?.resellerAgreementData.dimensions ||
    []) as CombinedFormDimension[]

  const productIdToUse = combinedFormData?.resellerAgreementData.awsProductId
  const products = useSelector<Product[]>(
    state => state.productsListing['AWS']?.products?.rows || []
  )
  const product =
    products.find(product => product.productId === productIdToUse) ||
    ({} as Product)
  const currencySymbol =
    getCurrencySymbol(
      combinedFormData?.resellerAgreementData.currency as string
    ) || '$'

  const allDimensions = useMemo(() => {
    return product?.productDimensions
  }, [product])

  const getAdditionalUsagePrice: any = (dimensionId: string) => {
    const matchedProductDimension = (allDimensions as any[])?.find(
      pd => pd.id === dimensionId
    )
    const additionalUsagePrice =
      matchedProductDimension?.prices?.additionalUsagePrice
    const subscriptionPrice = matchedProductDimension?.prices?.subscriptionPrice
    return additionalUsagePrice || subscriptionPrice
  }

  return (
    <BoxV4
      style={{ padding: '32px' }}
      heading={'Usage plans'}
      headingIcon={
        <FontAwesomeIcon
          icon={faRuler}
          style={{ width: '18px', height: '14px' }}
        />
      }
      variant="small"
      headingVariant="small"
    >
      <div className={clsx(classes.root, classes.elementsGap)}>
        <div className={classes.titleWraper}>
          <Typography className={clsx(classes.sectionText, classes.columnName)}>
            Dimensions
          </Typography>
          <Typography className={clsx(classes.sectionText, classes.columnDesc)}>
            Public price
          </Typography>
          <Typography
            className={clsx(classes.sectionText, classes.columnPrice)}
          >
            Price to ISV
          </Typography>
          {isCp && (
            <Typography
              className={clsx(
                classes.sectionText,
                classes.BuyerPrice,
                classes.centerAlign
              )}
            >
              Price from buyer
            </Typography>
          )}
        </div>

        <div className={classes.border}></div>
        {dimensions
          .filter(dimension =>
            productType !== PlanType.SAAS_SUBSCRIPTION
              ? dimension.type === 'additional_usage'
              : true
          )
          .map((dimension, index) => {
            const dimensionKey = dimension.dimensionId || index
            const productAdditionalUsagePrice = getAdditionalUsagePrice(
              dimension.dimensionId as string
            )
            const filterdDimensions = dimensions.filter(dimension =>
              productType !== PlanType.SAAS_SUBSCRIPTION
                ? dimension.type === 'additional_usage'
                : true
            )

            return (
              <div key={dimensionKey}>
                <div
                  className={classes.resourceRow}
                  data-testid="dimensions-row"
                >
                  <div className={classes.resourcesContainer}>
                    <div className={classes.columnName}>
                      <div
                        className={clsx(
                          classes.alignDescription,
                          classes.descHeightWidth,
                          classes.textClass
                        )}
                        data-testid="dimension-name"
                      >
                        <ShortenedNameV2
                          name={dimension.name || '-'}
                          length={20}
                        />
                      </div>
                    </div>

                    <div
                      className={clsx(
                        classes.alignDescription,
                        classes.wordWrap,
                        classes.columnDesc
                      )}
                      data-testid="description-select"
                    >
                      <Typography
                        className={classes.textClass}
                        style={{ display: 'flex' }}
                      >
                        <span className={classes.currency}>
                          {currencySymbol}
                        </span>
                        {productAdditionalUsagePrice}
                      </Typography>
                    </div>

                    <div
                      className={clsx(
                        classes.columnPrice,
                        classes.alignDescription
                      )}
                    >
                      {productType !== PlanType.SAAS_SUBSCRIPTION ? (
                        <Typography
                          className={classes.textClass}
                          style={{ display: 'flex' }}
                        >
                          <span className={classes.currency}>
                            {currencySymbol}
                          </span>
                          {dimensions?.[index]?.prices?.additionalUsagePrice ??
                            '-'}
                        </Typography>
                      ) : (
                        <Typography
                          className={classes.textClass}
                          style={{ display: 'flex' }}
                        >
                          <span className={classes.currency}>
                            {currencySymbol}
                          </span>
                          {dimensions?.[index]?.prices?.subscriptionPrice ??
                            '-'}
                        </Typography>
                      )}
                    </div>

                    {isCp && (
                      <div
                        className={clsx(
                          classes.alignDescription,
                          classes.wordWrap,
                          classes.BuyerPrice
                        )}
                      >
                        <Typography
                          className={classes.textClass}
                          style={{ display: 'flex' }}
                        >
                          <span className={classes.currency}>
                            {currencySymbol}
                          </span>
                          {(() => {
                            const basePrice =
                              productType === PlanType.SAAS_SUBSCRIPTION
                                ? Number(
                                    dimension?.prices?.subscriptionPrice ?? 0
                                  )
                                : Number(
                                    dimension?.prices?.additionalUsagePrice ?? 0
                                  )

                            const adjustment = Number(
                              combinedFormData?.cppoData
                                ?.adjustmentPercentage ?? 0
                            )

                            const value =
                              (basePrice * adjustment) / 100 + basePrice

                            return Number.isInteger(value)
                              ? value
                              : value.toFixed(2)
                          })()}
                        </Typography>
                      </div>
                    )}
                  </div>
                </div>
                {index !== filterdDimensions.length - 1 && (
                  <div className={classes.border}></div>
                )}
              </div>
            )
          })}
      </div>
    </BoxV4>
  )
}
